import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FadeUp } from "@/components/Reveal";

const FAQS = [
  {
    q: "Do I need to be in Seattle to work with you?",
    a: "Not at all. We are based in Seattle, but the practice is remote-capable from the ground up. Secure cloud tools, scheduled video check-ins, and a protected client portal mean working together feels the same whether you are across town or across the country.",
  },
  {
    q: "What's the difference between bookkeeping, accounting, and audit preparation?",
    a: "Bookkeeping is the day-to-day recording — transactions, reconciliations, payables and receivables. Accounting turns those records into statements, closes, and insight you can act on. Audit preparation makes sure that when an auditor arrives, every workpaper, schedule, and reconciliation is already in place. Most clients need a blend, and we scope that together.",
  },
  {
    q: "Do you have experience with tribal governments?",
    a: "Yes — it is a core part of our practice. We support tribal finance departments with fund accounting, grant tracking, indirect cost rates, and Uniform Guidance single audit preparation, paced to council calendars and handled with the discretion these engagements deserve.",
  },
  {
    q: "What happens in the first consultation?",
    a: "A relaxed 30-minute conversation. You tell us where your finances stand and what is keeping you up at night; we listen, ask a few questions, and outline a clear next step. No jargon, no pressure, no obligation — and no fee.",
  },
  {
    q: "How do we share documents safely?",
    a: "Through your secure client portal (powered by Financial Cents). Documents, requests, and messages live in one encrypted, organized place — no sensitive attachments floating around email inboxes.",
  },
  {
    q: "How much does it cost?",
    a: "Every engagement is scoped after our first conversation, and most ongoing work runs on a fixed monthly fee — so you always know the number before we begin, and there are no surprise invoices.",
  },
  {
    q: "How quickly do you respond?",
    a: "Within one business day as standard — and same-day during filing deadlines and audit fieldwork, when speed matters most.",
  },
];

export const Faq = () => (
  <section data-testid="faq-section" className="border-y border-black/10 bg-white py-24 lg:py-32">
    <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <FadeUp>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
              <span className="h-px w-10 bg-pine" />
              Before you call
            </p>
            <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
              Questions, <em className="text-pine">answered.</em>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-600">
              The things new clients usually ask before their first conversation with us —
              answered plainly.
            </p>
          </FadeUp>
        </div>
      </div>

      <div className="lg:col-span-7">
        <FadeUp delay={0.1}>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                data-testid={`faq-item-${i + 1}`}
                className="border-black/10"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${i + 1}`}
                  className="py-6 text-left font-serif text-lg tracking-tight text-ink transition-colors hover:text-pine hover:no-underline sm:text-xl"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="max-w-2xl pb-6 text-sm leading-relaxed text-zinc-600 sm:text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
      </div>
    </div>
  </section>
);
